import { type Maybe } from '@amazon/vinyl-util'

export enum TransitionKeys {
    MAIN_IMAGE = 'mainImage',
}

export const VIEW_TRANSITION_SOURCE_CLASS = 'viewTransitionSource'

// Source elements we've stamped with a shared-element `view-transition-name`.
// The name must be stripped once its transition is spent or superseded — a
// stale name pulled into the NEXT capture stacks a ghost image (and a duplicate
// name aborts the capture), which is what rapid multi-navigation triggered.
const preparedSources = new Set<HTMLElement>()

export function prepareViewTransition(target: HTMLElement, key: TransitionKeys, id: Maybe<string>) {
    const name = getViewTransitionName(key, id)
    // A fresh navigation supersedes any prior prepared source, so clear those
    // first — otherwise both names enter this capture.
    clearPreparedViewTransitions()
    // Ensure no unmanaged element (e.g. a hero already on the page) still holds
    // this exact name, which would collide in the capture.
    const previousEl: HTMLElement | null = document.querySelector(
        `[style*="view-transition-name: ${name}"]`
    )
    previousEl?.style.removeProperty('view-transition-name')
    target.style.setProperty('view-transition-name', name)
    target.classList.add(VIEW_TRANSITION_SOURCE_CLASS)
    preparedSources.add(target)
}

/**
 * Strip the `view-transition-name` from every source we stamped. Called when a
 * transition settles (`startViewTransition`) and when a newer
 * `prepareViewTransition` supersedes the prior one, so spent names never stack.
 */
export function clearPreparedViewTransitions() {
    preparedSources.forEach((el) => {
        el.style.removeProperty('view-transition-name')
        el.classList.remove(VIEW_TRANSITION_SOURCE_CLASS)
    })
    preparedSources.clear()
}

export function getViewTransitionName(key: TransitionKeys, id: Maybe<string>) {
    return `${key}-${id}`
}
